import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Modal, Button } from "react-bootstrap";
import {
  getDataAPI,
  postDataAPI,
  deleteDataAPI,
} from "../../../services/apiWrapper";
import { ALERT } from "../../../store/actions/actionTypes";
import CourseCascade from "./CourseCascade";

/**
 * LMS - Exam setup (Council/Admin + Faculty).
 *
 * Define an exam for a course. A NEW exam can be created for SEVERAL subjects at
 * once (tick them) - one exam per subject, each titled "<title> - <subject>".
 * For a one-time (course_wise) course you can also mark the exam "auto": it has no
 * fixed date and opens for each student once their course duration completes.
 */
export default function Exams() {
  const dispatch = useDispatch();
  const token = () => localStorage.getItem("adminToken");
  const alert = (payload) => dispatch({ type: ALERT, payload });

  const [subjects, setSubjects] = useState([]);
  const [courseId, setCourseId] = useState("");
  const [courseObj, setCourseObj] = useState(null);
  const [exams, setExams] = useState([]);

  // Point 9: per-course late penalty (council).
  const blankPenalty = { enroll_cutoff: "", enroll_amount: 0, examform_cutoff: "", examform_amount: 0, status: 1 };
  const [penalty, setPenalty] = useState(blankPenalty);
  const [penaltyBusy, setPenaltyBusy] = useState(false);

  const blank = {
    id: null, subject_id: "", subject_ids: [], subjectMeta: {}, auto: false, title: "",
    exam_date: "", exam_time: "",
    duration_minutes: 30, total_questions: 20, marks_per_question: 1, passing_percent: 40,
  };
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(blank);

  // Keyed ONLY off exam_type. course_type "1" is not reliable (some yearly
  // courses also use it), and the "auto" exam is for one-time courses only.
  const isCourseWise = courseObj && courseObj.exam_type === "course_wise";

  const onCourse = async (cid, obj) => {
    setCourseId(cid);
    setCourseObj(obj || null);
    setSubjects([]);
    setExams([]);
    setPenalty(blankPenalty);
    if (!cid) return;
    try {
      const [subs, ex, pen] = await Promise.all([
        dispatch(getDataAPI(`admin/lms/content/subjects/${cid}`, token())),
        dispatch(getDataAPI(`admin/lms/exams?course_id=${cid}`, token())),
        dispatch(getDataAPI(`admin/lms/penalty?course_id=${cid}`, token())),
      ]);
      setSubjects(subs.data.data || []);
      setExams(ex.data.data || []);
      const p = pen.data.data || {};
      setPenalty({
        enroll_cutoff: p.enroll_cutoff || "",
        enroll_amount: p.enroll_amount || 0,
        examform_cutoff: p.examform_cutoff || "",
        examform_amount: p.examform_amount || 0,
        status: typeof p.status === "undefined" ? 1 : p.status,
      });
    } catch (err) {
      alert({ error: "Could not load exams." });
    }
  };

  const savePenalty = async () => {
    setPenaltyBusy(true);
    try {
      const res = await dispatch(postDataAPI("admin/lms/penalty", { course_id: courseId, ...penalty }, token()));
      alert({ success: res.data.message || "Late penalty saved." });
    } catch (err) {
      alert({ error: err.response?.data?.message || "Could not save penalty." });
    }
    setPenaltyBusy(false);
  };

  const reloadExams = async () => {
    const ex = await dispatch(getDataAPI(`admin/lms/exams?course_id=${courseId}`, token()));
    setExams(ex.data.data || []);
  };

  const openAdd = () => {
    setForm(blank);
    setShow(true);
  };
  const openEdit = (x) => {
    setForm({
      id: x.id, subject_id: x.subject_id || "", subject_ids: [], subjectMeta: {},
      auto: Number(x.auto_duration) === 1,
      title: x.title, exam_date: x.exam_date || "", exam_time: x.exam_time || "",
      duration_minutes: x.duration_minutes,
      total_questions: x.total_questions, marks_per_question: x.marks_per_question,
      passing_percent: x.passing_percent,
    });
    setShow(true);
  };

  const toggleSubject = (id) => {
    const sid = Number(id);
    setForm((p) => {
      const has = p.subject_ids.includes(sid);
      const subject_ids = has ? p.subject_ids.filter((x) => x !== sid) : [...p.subject_ids, sid];
      const subjectMeta = { ...p.subjectMeta };
      if (has) delete subjectMeta[sid];
      else subjectMeta[sid] = subjectMeta[sid] || { exam_date: "", exam_time: "" };
      return { ...p, subject_ids, subjectMeta };
    });
  };

  // Per-subject date/time for a Semester/Yearly bulk create (Point 4).
  const setSubjectMeta = (sid, key, val) => {
    setForm((p) => ({
      ...p,
      subjectMeta: { ...p.subjectMeta, [sid]: { ...(p.subjectMeta[sid] || {}), [key]: val } },
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const body = {
        ...form,
        course_id: courseId,
        auto_duration: isCourseWise && form.auto ? 1 : 0,
      };
      if (form.id) {
        // Edit one exam, keep/adjust its subject + date + time.
        body.subject_id = form.subject_id || 0;
      } else if (isCourseWise) {
        // Point 3: course-wise is ONE exam covering ALL subjects (whole course).
        body.subject_ids = [];
        body.subject_id = 0;
      } else {
        // Point 4: Semester/Yearly - one exam per ticked subject, each with its
        // own date + (display-only) time.
        body.subjects = form.subject_ids.map((sid) => ({
          subject_id: sid,
          exam_date: (form.subjectMeta[sid] && form.subjectMeta[sid].exam_date) || form.exam_date || null,
          exam_time: (form.subjectMeta[sid] && form.subjectMeta[sid].exam_time) || form.exam_time || null,
        }));
        body.subject_id = 0;
      }
      const res = await dispatch(postDataAPI("admin/lms/exam", body, token()));
      alert({ success: res.data.message || "Exam saved." });
      setShow(false);
      reloadExams();
    } catch (err) {
      alert({ error: err.response?.data?.message || "Could not save exam." });
    }
  };

  const remove = async (x) => {
    if (!window.confirm(`Delete exam "${x.title}"?`)) return;
    try {
      await dispatch(deleteDataAPI(`admin/lms/exam/${x.id}`, token()));
      alert({ success: "Exam removed." });
      reloadExams();
    } catch (err) {
      alert({ error: err.response?.data?.message || "Could not delete." });
    }
  };

  return (
    <div className="container-fluid" style={{ padding: 20 }}>
      <h4 className="mb-3">Exams</h4>

      <CourseCascade onCourse={(id, obj) => onCourse(id, obj)} />

      {courseId ? (
        <>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 style={{ margin: 0 }}>Exam list</h5>
            <Button size="sm" variant="primary" onClick={openAdd}>+ Add Exam</Button>
          </div>
          <table className="table table-bordered bg-white">
            <thead>
              <tr>
                <th>Title</th>
                <th>Subject</th>
                <th>Date</th>
                <th>Duration</th>
                <th>Questions (pool)</th>
                <th>Pass %</th>
                <th style={{ width: 150 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {exams.length === 0 ? (
                <tr><td colSpan={7} className="text-center text-muted">No exams yet.</td></tr>
              ) : (
                exams.map((x) => (
                  <tr key={x.id}>
                    <td>{x.title}</td>
                    <td>{x.subject_name || (x.subject_id ? x.subject_id : "Whole course")}</td>
                    <td>{Number(x.auto_duration) === 1 ? <span className="badge badge-info">Auto (after course duration)</span> : (<>{x.exam_date || "—"}{x.exam_time ? <span className="text-muted"> · {x.exam_time}</span> : null}</>)}</td>
                    <td>{x.duration_minutes} min</td>
                    <td>
                      {x.total_questions}
                      {typeof x.pool !== "undefined" && (
                        <span className={x.pool < x.total_questions ? "text-danger" : "text-muted"}>
                          {" "}/ {x.pool} in bank
                        </span>
                      )}
                    </td>
                    <td>{x.passing_percent}%</td>
                    <td>
                      <Button size="sm" variant="outline-primary" className="mr-2" onClick={() => openEdit(x)}>Edit</Button>{" "}
                      <Button size="sm" variant="outline-danger" onClick={() => remove(x)}>Delete</Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Point 9: per-course late penalty. A cutoff date + fixed amount for
              late enrollment and for late exam-form submission. The student pays
              it on top of the exam fee. Leave an amount 0 to charge nothing. */}
          <div className="card mt-3" style={{ maxWidth: 720 }}>
            <div className="card-header py-2"><b>Late Penalty</b> <small className="text-muted">(this course)</small></div>
            <div className="card-body">
              <div className="form-row">
                <div className="form-group col-md-6">
                  <label>Late enrollment — after date</label>
                  <input type="date" className="form-control" value={penalty.enroll_cutoff || ""} onChange={(e) => setPenalty({ ...penalty, enroll_cutoff: e.target.value })} />
                </div>
                <div className="form-group col-md-6">
                  <label>Penalty amount (₹)</label>
                  <input type="number" min="0" className="form-control" value={penalty.enroll_amount} onChange={(e) => setPenalty({ ...penalty, enroll_amount: e.target.value })} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group col-md-6">
                  <label>Late exam-form — after date</label>
                  <input type="date" className="form-control" value={penalty.examform_cutoff || ""} onChange={(e) => setPenalty({ ...penalty, examform_cutoff: e.target.value })} />
                </div>
                <div className="form-group col-md-6">
                  <label>Penalty amount (₹)</label>
                  <input type="number" min="0" className="form-control" value={penalty.examform_amount} onChange={(e) => setPenalty({ ...penalty, examform_amount: e.target.value })} />
                </div>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <label style={{ fontWeight: 400, margin: 0 }}>
                  <input type="checkbox" checked={Number(penalty.status) === 1} onChange={(e) => setPenalty({ ...penalty, status: e.target.checked ? 1 : 0 })} style={{ marginRight: 8 }} />
                  Penalty active
                </label>
                <Button size="sm" variant="primary" disabled={penaltyBusy} onClick={savePenalty}>
                  {penaltyBusy ? "Saving…" : "Save penalty"}
                </Button>
              </div>
              <small className="text-muted">A student who enrols or submits the exam form after the date pays the matching amount with the exam fee (gateway).</small>
            </div>
          </div>
        </>
      ) : (
        <p className="text-muted">Choose a course to set up its exams.</p>
      )}

      <Modal show={show} onHide={() => setShow(false)}>
        <form onSubmit={save}>
          <Modal.Header closeButton>
            <Modal.Title>{form.id ? "Edit Exam" : "Add Exam"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="form-group">
              <label>Title</label>
              <input className="form-control" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
            </div>

            {form.id ? (
              <div className="form-group">
                <label>Subject (leave "Whole course" for a course-wide exam)</label>
                <select className="form-control" value={form.subject_id} onChange={(e) => setForm({ ...form, subject_id: e.target.value })}>
                  <option value="">Whole course</option>
                  {subjects.map((s) => (
                    <option key={s.id} value={s.id}>{s.name} {s.code ? `(${s.code})` : ""}</option>
                  ))}
                </select>
              </div>
            ) : isCourseWise ? (
              // Point 3: a course-wise course is ONE exam over ALL subjects. No
              // subject to choose - the paper is split across every subject
              // (min 10 questions each) and the marksheet scores each out of 100
              // (50 where the subject has a practical).
              <div className="alert alert-info" style={{ fontSize: 13 }}>
                <b>Course-wise exam:</b> one exam covers <b>all subjects</b> of this course.
                The paper is split across the subjects (at least 10 questions each) and the
                student sits it once.
              </div>
            ) : (
              // Point 4: Semester/Yearly - pick subjects and give EACH its own
              // date and start time. One exam is created per subject.
              <div className="form-group">
                <label>Subjects — tick each one and set its own exam date &amp; time</label>
                <div style={{ maxHeight: 260, overflowY: "auto", border: "1px solid #ddd", borderRadius: 4, padding: 10 }}>
                  {subjects.length === 0 ? (
                    <span className="text-muted">No subjects in this course — a whole-course exam will be created.</span>
                  ) : (
                    subjects.map((s) => {
                      const sid = Number(s.id);
                      const on = form.subject_ids.includes(sid);
                      const meta = form.subjectMeta[sid] || {};
                      return (
                        <div key={s.id} style={{ borderBottom: "1px solid #f0f0f0", padding: "6px 0" }}>
                          <label style={{ display: "block", marginBottom: on ? 6 : 0, fontWeight: 400 }}>
                            <input type="checkbox" checked={on} onChange={() => toggleSubject(s.id)} style={{ marginRight: 8 }} />
                            {s.name} {s.code ? `(${s.code})` : ""}
                            {Number(s.practical) === 1 ? <span className="badge badge-warning ml-2">Practical (exam /50)</span> : null}
                          </label>
                          {on && (
                            <div className="form-row" style={{ marginLeft: 24 }}>
                              <div className="form-group col mb-1">
                                <input type="date" className="form-control form-control-sm" value={meta.exam_date || ""} onChange={(e) => setSubjectMeta(sid, "exam_date", e.target.value)} />
                              </div>
                              <div className="form-group col mb-1">
                                <input type="time" className="form-control form-control-sm" value={meta.exam_time || ""} onChange={(e) => setSubjectMeta(sid, "exam_time", e.target.value)} title="Shown on the admit card only" />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
                <small className="text-muted">
                  Selected: {form.subject_ids.length || "none"} · time is shown on the admit card only (it does not restrict when the student can sit the exam).
                </small>
              </div>
            )}

            {isCourseWise && (
              <div className="form-group">
                <label style={{ fontWeight: 400 }}>
                  <input
                    type="checkbox"
                    checked={form.auto}
                    onChange={(e) => setForm({ ...form, auto: e.target.checked })}
                    style={{ marginRight: 8 }}
                  />
                  Auto (one-time course): no fixed date — opens for each student after their course
                  duration completes and fees are paid.
                </label>
              </div>
            )}

            {/* Single date + time: for an edit or a course-wise exam (Semester/
                Yearly set the date/time per subject above). Auto exams have none. */}
            {(form.id || isCourseWise) && !form.auto && (
              <div className="form-row">
                <div className="form-group col">
                  <label>Exam date</label>
                  <input type="date" className="form-control" value={form.exam_date || ""} onChange={(e) => setForm({ ...form, exam_date: e.target.value })} />
                </div>
                <div className="form-group col">
                  <label>Start time <small className="text-muted">(admit card only)</small></label>
                  <input type="time" className="form-control" value={form.exam_time || ""} onChange={(e) => setForm({ ...form, exam_time: e.target.value })} />
                </div>
              </div>
            )}

            <div className="form-row">
              <div className="form-group col">
                <label>Duration (minutes)</label>
                <input type="number" min="1" className="form-control" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} required />
              </div>
              <div className="form-group col">
                <label>Total questions</label>
                <input type="number" min="1" className="form-control" value={form.total_questions} onChange={(e) => setForm({ ...form, total_questions: e.target.value })} required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group col">
                <label>Marks / question</label>
                <input type="number" min="1" className="form-control" value={form.marks_per_question} onChange={(e) => setForm({ ...form, marks_per_question: e.target.value })} />
              </div>
              <div className="form-group col">
                <label>Passing %</label>
                <input type="number" min="0" max="100" className="form-control" value={form.passing_percent} onChange={(e) => setForm({ ...form, passing_percent: e.target.value })} />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShow(false)}>Cancel</Button>
            <Button variant="primary" type="submit">Save</Button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
}
